<template>
  <div class="cf-flex cf-flex--column cf-flex--gap-10">
    <!-- NOTE: The "name" and "autocomplete" attributes might look funny and inconsistent
    but are driven by special values required by browsers to autocomplete:
    https://stackoverflow.com/questions/7223168/how-to-trigger-autofill-in-google-chrome -->

    <cf-text-input
      id="frmNameCC"
      v-model="cardDetails.nameOnCard"
      class="cc-name"
      type="text"
      label="Name on Card"
      name="ccname"
      autocomplete="cc-name"
      :validations="{
        full_name: true,
        required: !skipRequiredValidation
      }"
      @focus="inputFocusHandler"
    />

    <cf-text-input
      id="frmCCNum"
      v-model="cardDetails.cardNumber"
      class="cc-number"
      icon-class="fa-credit-card"
      type="card"
      label="Credit Card Number"
      name="cardnumber"
      autocomplete="cc-number"
      data-three-ds="pan"
      :validations="{
        required: !skipRequiredValidation
      }"
      @focus="inputFocusHandler"
    />

    <div class="cf-flex cf-flex--card-details">
      <cf-text-input
        id="frmCCExp"
        v-model="cardDetails.cardExpiration"
        class="cc-expiration"
        type="text"
        label="Expiration"
        name="exp-date"
        mask="monthAndDate"
        inputmode="numeric"
        placeholder="MM/YY"
        autocomplete="cc-exp"
        :validations="{
          required: !skipRequiredValidation,
          card_expiration: true
        }"
        @focus="inputFocusHandler"
      />

      <cf-text-input
        id="frmCCCVC"
        v-model="cardDetails.cvv"
        class="cc-cvv"
        type="cvv"
        label="CVV"
        name="cvc"
        autocomplete="cc-csc"
        :validations="{
          required: !skipRequiredValidation,
          cvv: {
            cardNumber: cardDetails.cardNumber
          }
        }"
        @focus="inputFocusHandler"
      />

      <cf-text-input
        v-if="!requireBillingAddress"
        v-model="billingAddress.postalCode"
        class="cc-zip"
        type="text"
        label="Postal Code"
        name="zip"
        data-three-ds="billingPostalCode"
        mask="postalCode"
        :validations="{
          required: !skipRequiredValidation,
          postal_code: true
        }"
        @focus="inputFocusHandler"
      />
    </div>

    <!-- These hidden inputs are needed for Paay 3ds auth https://docs.3dsintegrator.com/docs/dom-elements -->
    <template v-if="renderPaayTdsInputs">
      <cf-text-input
        v-show="false"
        type="text"
        label="month"
        name="month"
        :model-value="tdsMonth"
        data-three-ds="month"
      />

      <cf-text-input
        v-show="false"
        type="text"
        label="year"
        name="year"
        :model-value="tdsYear"
        data-three-ds="year"
      />

      <!--
        Hidden Address/Shipping details. Only rendered for AMEX cards: for
        non-AMEX cards, sending the store's address as the shipping address
        (the default for dine-in/pickup) triggers AVS mismatch declines.
        The Paay SDK is initialized lazily at submit time (see the parent
        cf-payment-information component) so the SDK scans the form with
        whatever shipping inputs Vue has rendered at that moment.
      -->
      <template v-if="isAmex">
        <cf-text-input
          v-show="false"
          type="text"
          label="shippingLine1"
          name="shippingLine1"
          :model-value="shippingDetails.line1"
          data-three-ds="shippingLine1"
        />

        <cf-text-input
          v-show="false"
          type="text"
          label="shippingLine2"
          name="shippingLine2"
          :model-value="shippingDetails.line2"
          data-three-ds="shippingLine2"
        />

        <cf-text-input
          v-show="false"
          type="text"
          label="shippingLine3"
          name="shippingLine3"
          :model-value="shippingDetails.line3"
          data-three-ds="shippingLine3"
        />

        <cf-text-input
          v-show="false"
          type="text"
          label="shippingPostCode"
          name="shippingPostCode"
          :model-value="shippingDetails.postalCode"
          data-three-ds="shippingPostCode"
        />

        <cf-text-input
          v-show="false"
          type="text"
          label="shippingCity"
          name="shippingCity"
          :model-value="shippingDetails.city"
          data-three-ds="shippingCity"
        />

        <cf-text-input
          v-show="false"
          type="text"
          label="shippingState"
          name="shippingState"
          :model-value="shippingDetails.state"
          data-three-ds="shippingState"
        />

        <cf-text-input
          v-show="false"
          type="text"
          label="shippingCountry"
          name="shippingCountry"
          :model-value="shippingDetails.countryCode"
          data-three-ds="shippingCountry"
        />

        <!--
          merchantRiskIndicator inputs. Gated alongside the shipping inputs
          because the indicator object only makes sense when a shipping
          address is being sent (the indicators describe HOW the order is
          shipping). Paay's SDK only emits the merchantRiskIndicator object
          if these inputs are in the DOM at construction time, so wrapping
          them in this v-if keeps them out of the non-AMEX payload.
        -->
        <cf-text-input
          v-show="false"
          model-value="04"
          type="text"
          label="shipIndicator"
          name="shipIndicator"
          data-three-ds="shipIndicator"
        />

        <cf-text-input
          v-show="false"
          model-value="02"
          type="text"
          label="deliveryTimeFrame"
          name="deliveryTimeFrame"
          data-three-ds="deliveryTimeFrame"
        />

        <cf-text-input
          v-show="false"
          model-value="01"
          type="text"
          label="reorderItemsInd"
          name="reorderItemsInd"
          data-three-ds="reorderItemsInd"
        />
      </template>

    </template>

    <div
      v-if="requireBillingAddress"
      class="cf-flex cf-flex--column cf-flex--gap-12"
    >
      <h4 class="cf-title cf-title--4 cf-text-align-left">
        Billing Details
      </h4>

      <div class="cf-flex cf-flex--column cf-flex--gap-10">
        <cf-text-input
          v-show="false"
          model-value="840"
          type="text"
          label="billingCountry"
          name="billingCountry"
          data-three-ds="billingCountry"
        />

        <cf-text-input
          v-model="billingAddress.line1"
          type="text"
          autocomplete="address-line1"
          label="Street Address"
          placeholder="Street Address"
          name="addressLine1"
          data-three-ds="billingLine1"
          class="address-line-1"
          @focus="inputFocusHandler"
        />

        <cf-text-input
          v-model="billingAddress.line2"
          type="text"
          autocomplete="address-line2"
          label="Address Line 2"
          placeholder="Suite or apartment #"
          name="addressLine2"
          data-three-ds="billingLine2"
          class="address-line-2"
          @focus="inputFocusHandler"
        />

        <div class="layout--address-line-3">
          <cf-text-input
            v-model="billingAddress.city"
            type="text"
            autocomplete="address-level2"
            label="City"
            name="city"
            data-three-ds="billingCity"
            :validations="{
              required: !skipRequiredValidation
            }"
            @focus="inputFocusHandler"
          />

          <cf-text-input
            v-model="billingAddress.state"
            type="text"
            autocomplete="address-level1"
            label="State"
            name="state"
            data-three-ds="billingState"
            allowed-characters="A-Za-z"
            :validations="{
              required: !skipRequiredValidation,
              length: 2
            }"
            @focus="inputFocusHandler"
          />

          <cf-text-input
            v-model="billingAddress.postalCode"
            type="text"
            label="Postal Code"
            name="zip"
            data-three-ds="billingPostCode"
            mask="postalCode"
            :validations="{
              required: !skipRequiredValidation,
              postal_code: true
            }"
            @focus="inputFocusHandler"
          />
        </div>
      </div>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';
  import detectCreditCardType from 'credit-card-type';

  import returnAppConfig from '@/helpers/return-app-config';

  import CfTextInput from '@/view/components/shared/cf-text-input/index.vue';


  export default {
    name: 'CfDefaultCreditCardForm',

    components: {
      CfTextInput
    },

    inject: {
      $validator: {
        from: '$validator'
      }
    },

    props: {
      isCheckoutFlow: {
        type: Boolean,
        required: false,
        default: true
      },

      requireBillingAddress: {
        type: Boolean,
        default: false
      },

      tdsProvider: {
        type: String,
        default: ''
      },

      skipRequiredValidation: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        cardDetails: {
          nameOnCard: null,
          cardNumber: null,
          cardExpiration: null,
          cvv: null
        },
        billingAddress: {
          city: null,
          line1: null,
          line2: null,
          state: null,
          postalCode: null
        },
        tdsYear: null,
        tdsMonth: null
      };
    },

    computed: {
      ...mapGetters('session', ['orderDetails']),
      ...mapGetters('locations', ['selectedLocation']),

      renderPaayTdsInputs() {
        return this.isCheckoutFlow && this.tdsProvider === 'Paay';
      },

      isAmex() {
        const cardNumber = (this.cardDetails.cardNumber || '').replace(/[^0-9]/g, '');
        if (!cardNumber) return false;
        const matches = detectCreditCardType(cardNumber);
        return matches.length === 1 && matches[0].type === 'american-express';
      },

      shippingDetails() {
        if (!this.isAmex) return null;

        const { isTestEnvironment } = returnAppConfig();

        const shippingDetails = {
          countryCode: 840,
          shipIndicator: '04'
        };

        switch (this.orderDetails.fulfillmentType) {
          case 'delivery':
          case 'shipping':
            Object.assign(shippingDetails, {
              line1: this.orderDetails.deliveryAddress.line1,
              line2: this.orderDetails.deliveryAddress.line2,
              postalCode: this.orderDetails.deliveryAddress.postalCode,
              city: this.orderDetails.deliveryAddress.city,
              state: this.orderDetails.deliveryAddress.state,
              shipIndicator: '03'
            });
            break;
          default:
            Object.assign(shippingDetails, {
              line1: this.selectedLocation.address.line1,
              line2: this.selectedLocation.address.line2,
              postalCode: this.selectedLocation.address.postalCode,
              city: this.selectedLocation.address.city,
              state: this.selectedLocation.address.state
            });
            break;
        }
        /** This is used for testing purposes so QA can validate */
        // eslint-disable-next-line no-console
        if (isTestEnvironment && this.renderPaayTdsInputs) console.log(shippingDetails);

        return shippingDetails;
      }
    },

    watch: {
      'cardDetails.cardExpiration': 'cardExpirationChangeHandler',
      'billingAddress.state': 'transformToUpperCase'
    },

    methods: {
      inputFocusHandler() {
        this.$emit('interacted');
      },

      cardExpirationChangeHandler(value) {
        const month = Number(value.substring(0, 2));
        const year = Number(`20${value.substring(3)}`);

        /** Update tds values needed by Paay 3ds sdk to authorize payment */
        this.tdsYear = year;
        this.tdsMonth = month;
      },

      transformToUpperCase(value) {
        if (value && typeof value === 'string') {
          const upperCaseValue = value.toUpperCase();
          if (upperCaseValue !== value) {
            this.billingAddress.state = upperCaseValue;
          }
        }
      },

      normalizeNameOnCreditCard() {
        const fullName = this.cardDetails.nameOnCard;
        const nameSegments = fullName.trim().split(' ');

        const updatedCardHolderName = {
          firstName: nameSegments.shift(),
          lastName: nameSegments.join(' '),
          fullName
        };

        return updatedCardHolderName;
      },

      normalizeCardNumber() {
        const cardNumber = this.cardDetails.cardNumber || '';
        const unformattedCardNumber = cardNumber.replace(/[^0-9]/g, '');
        const cardType = detectCreditCardType(unformattedCardNumber)[0].niceType;

        const upatedCardNumbers = {
          cardNumber: unformattedCardNumber,
          cardNumberLastFour: unformattedCardNumber.slice(-4),
          cardType
        };

        return upatedCardNumbers;
      },

      normalizeCardExpiration() {
        const { cardExpiration } = this.cardDetails;
        const month = Number(cardExpiration.substring(0, 2));
        const year = Number(`20${cardExpiration.substring(3)}`);

        const updatedCardExpiration = {
          month,
          year
        };

        return updatedCardExpiration;
      },

      normalizeCardDetails() {
        const nameOnCard = this.normalizeNameOnCreditCard();
        const { cardNumber, cardNumberLastFour, cardType } = this.normalizeCardNumber();
        const cardExpiration = this.normalizeCardExpiration();

        const updatedCardDetails = {
          nameOnCard,
          cardNumber,
          cardNumberLastFour,
          cardType,
          cvv: this.cardDetails.cvv,
          cardExpiration
        };

        return updatedCardDetails;
      },

      submit() {
        const cardDetails = this.normalizeCardDetails();

        const { postalCode, ...rest } = this.billingAddress;

        const billingAddress = {
          postalCode,
          ...this.requireBillingAddress && { ...rest }
        };

        const paymentMethod = {
          type: 'card',
          cardDetails,
          billingAddress
        };

        this.$emit('success', { paymentMethod });
      }
    }
  };
</script>
